'use client';

import { Box, Button } from '@mui/material';
import { StackRow } from '@ocodelib/mui';
import { useBehaviorSubject } from '@ocodelib/mui/hooks';
import { downloadBlob } from '@ocodelib/util/web';
import type { CodeProjectMeta, ProjectFile } from '@repo/project-interface';
import { DefaultProjectSerializer } from '@repo/project-serializer/impl';
import { usePyBasicApp } from '@repo/py-basic-app';
import clsx from 'clsx';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { BootstrapInput } from '../../../components/BootstrapInput';
import { LoginButton } from '../../../components/LoginButton';
import { LogoLinkButton } from '../../../components/LogoLinkButton';
import {
  ProjectPickerDialog,
  type ProjectPickerDialogProps,
} from '../../../dialogs/ProjectPickerDialog';
import { ProjectSaveDialog, type ProjectSaveDialogProps } from '../../../dialogs/ProjectSaveDialog';
import { LoginDialogOpenEvent } from '../../../events/login-dialog-open';
import { useLoginUser } from '../../../store/useAuthStore';
import { ProjectOpenButton } from './top-bar/ProjectOpenButton';
import { ProjectSaveButton } from './top-bar/ProjectSaveButton';

type DialogParams =
  | {
      id: 'ProjectSaveDialog';
      props: ProjectSaveDialogProps;
    }
  | {
      id: 'ProjectPickerDialog';
      props: ProjectPickerDialogProps;
    };

interface Props {
  title?: string;
  className?: string;
  backLink?: string;
}

export function WorkspaceTopbar(props: Props) {
  const { className, title } = props;
  const rootRef = useRef<HTMLElement | null>(null);
  const { app } = usePyBasicApp();
  const { profile } = useLoginUser();
  const [dialogParams, setDialogParams] = useState<DialogParams>();
  const projectName = useBehaviorSubject(app.projectName$);
  const projectKey = useBehaviorSubject(app.projectKey$);

  // 저장 다이얼로그 띄우기
  const openSaveDialog = (projectId: string | undefined, projectName: string, files: File[]) => {
    const createDataFile = () => DefaultProjectSerializer.createDataFile(projectName, files);

    setDialogParams({
      id: 'ProjectSaveDialog',
      props: {
        breed: 'py',
        open: true,
        onClose: () => setDialogParams(undefined),
        projectId,
        projectName,
        createDataFile,
        onSaved: (project) => {
          console.log('saved-project', project);
          setDialogParams(undefined);
          app.setProjectName(project.projectName);
          app.setProjectKey({
            projectId: project.projectId,
            accountId: project.accountId,
          });
        },
      },
    });
  };

  // 새작품으로 저장 버튼 클릭
  const handleClickSaveNew = () => {
    if (!profile) {
      toast.warn('작품 저장을 위해 로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    const files = app.files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return;
    }
    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });

    openSaveDialog(undefined, projectName, fileObjects);
  };

  const handleClickSaveUpdate = () => {
    if (!profile) {
      // 업데이트이므로 이런 경우가 발생하면 안됨
      toast.warn('작품 저장을 위해 로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    if (!projectKey) {
      // 업데이트이므로 이런 경우가 발생하면 안됨
      toast.warn('프로젝트 키가 존재하지 않습니다.');
      return;
    }

    const files = app.files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return;
    }
    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });

    openSaveDialog(projectKey.projectId, projectName, fileObjects);
  };

  // 파일 불러오기 버튼 클릭
  const handleClickOpenFromFileSelect = () => {
    //
  };

  const loadProjectFile = async (
    projectFileUrl: string,
  ): Promise<{
    meta: CodeProjectMeta;
    data: ProjectFile[];
  }> => {
    const pjtFile = await fetch(projectFileUrl).then((response) => response.blob());
    return await DefaultProjectSerializer.deserializePjtFile(pjtFile);
  };

  // 내작품에서 선택
  const handleClickOpenFromServer = () => {
    if (!profile) {
      toast.warn('로그인이 필요합니다.');
      setTimeout(() => {
        LoginDialogOpenEvent.emit(Date.now());
      }, 700);
      return;
    }

    setDialogParams({
      id: 'ProjectPickerDialog',
      props: {
        open: true,
        breed: 'py',
        disableBreedSelect: true,
        onClose: () => setDialogParams(undefined),
        onSelected: (pjt) => {
          setDialogParams(undefined);
          const { projectFileUrl } = pjt;
          console.log('pjt selected', pjt);
          loadProjectFile(projectFileUrl).then((selection) => {
            if (!selection) return;
            app.resetProject({
              breed: pjt.breed,
              project: {
                accountId: pjt.accountId,
                projectId: pjt.projectId,
              },
              projectName: selection.meta.projectName,
              data: selection.data,
            });
          });
        },
      },
    });
  };

  // 저장버튼의 서브메뉴에 저장(업데이트) 버튼을 표시할지 여부
  // 현재 작품의 소유자가 본인인 경우에만 업데이트 버튼을 표시
  const visibleUpdateBtn = Boolean(profile && profile.accountId == projectKey?.accountId);

  return (
    <Box
      ref={rootRef}
      className={clsx('WorkspaceTopbar-root', className)}
      sx={{
        position: 'relative',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: 'primary.contrastText',
        bgcolor: 'primary.dark',
        boxShadow: 'none',
      }}
    >
      <StackRow alignItems="center" spacing={1}>
        <LogoLinkButton href={props.backLink || '/coding/list'} title="파이썬" />
        <BootstrapInput
          value={projectName}
          size="small"
          autoComplete="off"
          onChange={(e) => {
            app.setProjectName(e.target.value);
          }}
        />

        <ProjectSaveButton
          visibleUpdateBtn={visibleUpdateBtn}
          onClickDownloadBtn={() => {
            app.serializeToPjtFile().then((zipFile) => {
              if (zipFile) {
                downloadBlob(zipFile, zipFile.name);
              }
            });
          }}
          onClickSaveNewBtn={() => {
            handleClickSaveNew();
          }}
          onClickSaveUpdateBtn={() => {
            handleClickSaveUpdate();
          }}
        />
      </StackRow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Button
          component="div"
          color="inherit"
          onClick={() => {
            app.resetToNewProject();
          }}
        >
          새로 만들기
        </Button>
        <ProjectOpenButton
          onClickOpenFromFileSelect={handleClickOpenFromFileSelect}
          onClickOpenFromServerBtn={handleClickOpenFromServer}
        />
        <Box
          sx={{
            position: 'relative',
            '&::before': {
              position: 'absolute',
              content: '""',
              top: '50%',
              left: 0,
              transform: 'translateY(-50%)',
              height: 16,
              width: '1px',
              bgcolor: '#fff4',
            },
          }}
        >
          <LoginButton whiteText />
        </Box>
      </Box>
      {dialogParams?.id === 'ProjectSaveDialog' && <ProjectSaveDialog {...dialogParams.props} />}
      {dialogParams?.id === 'ProjectPickerDialog' && (
        <ProjectPickerDialog {...dialogParams.props} />
      )}
    </Box>
  );
}
